import React from 'react';
import { EventsListUnified } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventsListUnified';
import { FiltersState } from 'src/domains/layouts/webview/components/filters/Filters.state';
import { observer } from 'src/utils/mobx-react';
import { EventsTableUnifiedWrapper } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventsTableMobX.style';
import { PriceChangeLegend } from 'src/domains/layouts/webview/components/priceChangeLegend/PriceChangeLegend';
import { useAppStateContext } from 'src/appState/AppState';
import { CompetitionId } from 'src_common/common/websocket2/id/WebsocketId';
import { useCommon } from 'src/domains/common/Common';
import { SportsFilters } from 'src/domains/sportsbook/webview/components/sportEventsListNew/SportsFilters/SportsFilters';
import {
    SportsFilterChipType,
    UnifiedSportMarketFilterStrategy,
    UnifiedSportTimeFilterStrategy,
} from 'src/domains/sportsbook/webview/components/sportEventsListNew/SportsFilters/UnifiedSportsFilters.state';
import { SportCompetitionsList } from 'src/domains/sportsbook/webview/components/sportEventsListNew/SportCompetitionsList/SportCompetitionsList';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';

interface EventsTableUnifiedPropsType {
    competitionId: CompetitionId | null;
    sportId: string;
}

export const EventsTableUnified = observer('EventsTableUnified', (props: EventsTableUnifiedPropsType) => {
    const { appSportsBookState } = useAppStateContext();
    const common = useCommon();
    const { competitionId, sportId: sportIdKey } = props;
    const starRouter = StarRouter.get(common);
    const competitionFilter =
        starRouter.currentView?.name === 'competition' ? starRouter.currentView.filter : undefined;
    const openOutrightsFilter = competitionFilter === 'outrights';

    const [sportFilterChip, setSportFilterChip] = React.useState<SportsFilterChipType>(
        openOutrightsFilter === true ? 'outrights' : 'featured'
    );

    const sportWideListState = appSportsBookState.unifiedSportEventsListState.get(sportIdKey).get(null);
    const eventsListState = appSportsBookState.unifiedSportEventsListState.get(sportIdKey).get(competitionId);
    const isCompetitionPage = competitionId !== null;

    React.useEffect(() => {
        if (!isCompetitionPage) {
            sportWideListState.setFilterInPlay(false);
            sportWideListState.setFilterOutrights(false);
            sportWideListState.setFilterTime(null);
            setSportFilterChip('featured');
        }
    }, [sportIdKey, sportWideListState, isCompetitionPage]);

    React.useEffect(() => {
        if (competitionId === null) {
            return;
        }

        if (openOutrightsFilter === true) {
            eventsListState.setFilterOutrights(true);
            setSportFilterChip('outrights');
            return;
        }

        eventsListState.clearSecondaryFilters();

        if (eventsListState.filter_market === null && eventsListState.marketForEnv.length > 0) {
            eventsListState.changeFilter_market(eventsListState.marketForEnv[0]?.id ?? null);
        }

        setSportFilterChip('featured');
    }, [competitionId, eventsListState, openOutrightsFilter]);

    const timeFiltersState = React.useMemo(() => {
        return new FiltersState(new UnifiedSportTimeFilterStrategy(common, eventsListState), 'unified-events-time');
    }, [common, eventsListState]);

    const marketFiltersState = React.useMemo(() => {
        return new FiltersState(new UnifiedSportMarketFilterStrategy(eventsListState), 'unified-events-market');
    }, [eventsListState]);

    const isCompetitionView = sportFilterChip === 'competition' && competitionId === null;

    const showMarketFilters =
        sportIdKey !== 'baseball' &&
        (isCompetitionPage
            ? eventsListState.filter_outrights === false && marketFiltersState.filters.length > 0
            : eventsListState.isMarketFilterActive === true &&
              sportFilterChip !== 'competition' &&
              marketFiltersState.filters.length > 0);

    return (
        <EventsTableUnifiedWrapper
            className='events-table--sport-list experiments-unified-events-list'
            data-testid='events-table-unified'
        >
            <SportsFilters
                eventsListState={eventsListState}
                sportWideListState={sportWideListState}
                timeFiltersState={timeFiltersState}
                marketFiltersState={marketFiltersState}
                sportFilterChip={sportFilterChip}
                onSportFilterChipChange={setSportFilterChip}
                isCompetitionPage={isCompetitionPage}
                showMarketFilters={showMarketFilters}
            />

            {isCompetitionView === true ? (
                <>
                    <SportCompetitionsList
                        sportId={sportIdKey}
                        sportWideListState={sportWideListState}
                    />
                    <PriceChangeLegend isRace={false} />
                </>
            ) : (
                <>
                    <EventsListUnified
                        eventsListState={eventsListState}
                        sportId={sportIdKey}
                    />

                    <PriceChangeLegend isRace={false} />
                </>
            )}
        </EventsTableUnifiedWrapper>
    );
});
